import {
    DEFAULT_IMAGE_SCALE,
    EXPORT_CONFIGS,
    type ExportOptionConfig,
    type ExportFormatType,
    type PublishProvidersType
} from "hoxton-shared/enums/exports";

import { apolloClient } from "@/apollo/index";

import type { JobNotification } from "@/store/modules/jobs";

import type { ConstructedDeliverable } from "@/types/types";

import publishVariationsXmlFeedMutation from "@/apollo/mutations/PublishVariationsXmlFeed.gql";
import unpublishVariationsXmlFeedMutation from "@/apollo/mutations/UnpublishVariationsXmlFeed.gql";
import addDownloadComputedDeliverablesJobMutation from "@/apollo/mutations/AddDownloadComputedDeliverablesJob.gql";
import publishChoreographFeedMutation from "@/apollo/mutations/PublishChoreographFeed.gql";

import getComputedDeliverableIdentifiers from "@/services/deliverables/getComputedDeliverableIdentifiers";
import type { ConstructedLibraryFilters } from "@/utils/constructLibraryFiltersQueryVariables";

type ComputedDeliverableIdentifiers = string[] | ReturnType<typeof getComputedDeliverableIdentifiers>;

type BatchMutationVariables = {
    campaignId: string;
    libraryFilters?: ConstructedLibraryFilters;
    computedDeliverableIdentifiers?: ComputedDeliverableIdentifiers;
};

type DownloadJobVariables = BatchMutationVariables & {
    exportFormat: ExportFormatType;
    platformOpts: Record<string, unknown>;
    deviceScaleFactor?: number;
};

function getApolloClient() {
    if (!apolloClient) {
        throw new Error("Apollo client is not initialized");
    }

    return apolloClient;
}

function getBatchMutationVariables({
    campaignId,
    deliverables,
    deliverableIdentifiers,
    libraryFilters
}: {
    campaignId: string;
    deliverables: ConstructedDeliverable[];
    deliverableIdentifiers: string[];
    libraryFilters: ConstructedLibraryFilters | null;
}) {
    const variables: BatchMutationVariables = {
        campaignId
    };

    if (libraryFilters && Object.keys(libraryFilters).length > 0) {
        // all selected modes
        variables.libraryFilters = libraryFilters;
    } else if (deliverableIdentifiers.length) {
        variables.computedDeliverableIdentifiers = deliverableIdentifiers;
    } else {
        const computedDeliverableIdentifiers = getComputedDeliverableIdentifiers(deliverables);
        variables.computedDeliverableIdentifiers = computedDeliverableIdentifiers;
    }

    return variables;
}

export async function publishDeliverables({
    campaignId,
    publishProvider,
    deliverables,
    libraryFilters,
    isRendering,
    rebuild
}: {
    campaignId: string;
    publishProvider: PublishProvidersType;
    deliverables: ConstructedDeliverable[];
    libraryFilters: ConstructedLibraryFilters | null;
    isRendering: boolean;
    rebuild: boolean;
}) {
    const variables = {
        ...getBatchMutationVariables({ campaignId, deliverables, deliverableIdentifiers: [], libraryFilters }),
        publishProvider
    };

    const { data } = await getApolloClient().mutate<{
        publishVariationsXmlFeed: JobNotification;
    }>({
        mutation: publishVariationsXmlFeedMutation,
        variables: { ...variables, isRendering, rebuild }
    });

    if (!data?.publishVariationsXmlFeed) {
        throw new Error("Failed to publish variations");
    }

    return data.publishVariationsXmlFeed;
}

export async function unpublishDeliverables({
    campaignId,
    publishProvider,
    deliverables,
    libraryFilters
}: {
    campaignId: string;
    publishProvider: PublishProvidersType;
    deliverables: ConstructedDeliverable[];
    libraryFilters: ConstructedLibraryFilters | null;
}) {
    const variables = {
        ...getBatchMutationVariables({ campaignId, deliverables, deliverableIdentifiers: [], libraryFilters }),
        publishProvider
    };

    try {
        const { data } = await getApolloClient().mutate<{
            unpublishVariationsXmlFeed: JobNotification;
        }>({
            mutation: unpublishVariationsXmlFeedMutation,
            variables
        });

        return data?.unpublishVariationsXmlFeed;
    } catch (error) {
        console.error("Error unpublishing variations:", error);
        throw error;
    }
}

export async function downloadJob({
    campaignId,
    exportFormat,
    deliverables,
    deliverableIdentifiers = [],
    libraryFilters = null,
    platformOpts = {}
}: {
    campaignId: string;
    exportFormat: ExportFormatType;
    deliverables: ConstructedDeliverable[];
    deliverableIdentifiers?: string[];
    libraryFilters?: ConstructedLibraryFilters | null;
    platformOpts?: Record<string, unknown>;
}) {
    const { imageScale, ...mutationPlatformOpts } = platformOpts as { imageScale?: number };

    const variables: DownloadJobVariables = {
        ...getBatchMutationVariables({
            campaignId,
            deliverables,
            deliverableIdentifiers,
            libraryFilters
        }),
        exportFormat,
        platformOpts: mutationPlatformOpts
    };

    const exportConfig: ExportOptionConfig = EXPORT_CONFIGS[exportFormat];

    // `imageScale` is the name the setting carries in the UI; `deviceScaleFactor` is the name it carries on the backend
    if (exportConfig.settings?.includes("imageScale")) {
        variables.deviceScaleFactor = imageScale ?? DEFAULT_IMAGE_SCALE;
    }

    const { data } = await getApolloClient().mutate<{
        addDownloadComputedDeliverablesJob: JobNotification;
    }>({
        mutation: addDownloadComputedDeliverablesJobMutation,
        variables
    });

    return data!.addDownloadComputedDeliverablesJob;
}

export async function publishChoreographFeed(jobId: string, uniqueIdsList: string[]) {
    const { data } = await getApolloClient().mutate<{
        publishChoreographFeed: JobNotification;
    }>({
        mutation: publishChoreographFeedMutation,
        variables: { jobId, uniqueIdsList }
    });
    return data!.publishChoreographFeed;
}
